import { clsx, type ClassValue } from "clsx";
import { extendTailwindMerge } from "tailwind-merge";

/**
 * The theme's own `text-*` names, split by what they actually are.
 *
 * `tailwind-merge` decides which classes conflict by parsing the class name, and it has no access
 * to `theme.css`. Every `text-…` looks alike to it, so out of the box it puts a size and a colour
 * in the same group and keeps only the last one written. `text-heading … text-success` came out as
 * `text-success` — the size silently deleted — which is why a green stat rendered at body size
 * beside a black one at heading size, and why a long amount was clipped to `$3,240.0(` in a box
 * built for the smaller text.
 *
 * Listing the names here is what makes the two groups distinguishable. Both lists must stay in step
 * with the `@theme` block in `theme.css`; a name missing from either one silently returns to the
 * old behaviour, so `cn.test.ts` asserts they match the stylesheet rather than trusting this
 * comment.
 */
const FONT_SIZES = ["caption", "body", "body-large", "title", "heading", "display"];

const TEXT_COLOURS = [
  "paper",
  "surface",
  "sunk",
  "line",
  "line-strong",
  "muted",
  "ink",
  "brand",
  "brand-contrast",
  "brand-secondary",
  "accent",
  "accent-surface",
  "accent-strong",
  "success",
  "success-surface",
  "warning",
  "warning-surface",
  "danger",
  "danger-surface",
  "pending",
  "pending-surface",
];

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      "font-size": [{ text: FONT_SIZES }],
      "text-color": [{ text: TEXT_COLOURS }],
    },
  },
});

/**
 * Merge class names, with later Tailwind utilities winning over earlier ones of the same kind.
 *
 * Without the merge step a caller's `px-6` and a component's default `px-4` both land in the class
 * list and the winner is whichever CSS rule was emitted last — which is a build-order detail, not a
 * decision. `twMerge` makes the last-written class win, which is what everyone assumes anyway.
 *
 * "Of the same kind" is doing the work in that sentence: a size and a colour are *not* the same
 * kind, and the merge only knows that because of the lists above.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/** Exported for the test that keeps these lists honest against `theme.css`. */
export const THEME_TEXT_NAMES = { fontSizes: FONT_SIZES, colours: TEXT_COLOURS };
